import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  isProjectLocationKind,
  LOCATION_KIND_PROPERTY,
} from '~/client/src/shared/resources/domain/locationKind'

// Stable identifiers for the resource control fields. They double as record.root
// field keys (permittedCompanies, operatingIntervals, isBookable) and as table
// column / Controls-tab field ids. WORKFLOW_TYPES_KEY is an id only: workflow
// types live on each permit-type field's FilterOptions, not on the record.
// Shared so both the desktop controls table and the shared Controls tab
// (desktop + mobile) reference one set.
export const WORKFLOW_TYPES_KEY = 'workflowTypes'
export const PERMITTED_COMPANIES_KEY = 'permittedCompanies'
export const MAX_BOOKING_DURATION_KEY = 'maxBookingDurations'
export const MAX_OVERLAPPING_KEY = 'maxOverlappingBookings'
export const HOURS_OF_OPERATION_KEY = 'operatingIntervals'
export const DATE_CLOSURES_KEY = 'closures'
export const WORKFLOW_RESOURCE_KEY = 'isBookable'
export const SHOW_IN_MAP_KEY = 'showInMap'

// The controls a resource can expose, in display order. The WR toggle
// (isBookable) is always present and is not listed here. Equipment exposes them
// all; Materials expose only Workflow Types and Permitted Companies. `showInMap`
// is an independent display flag (equipment only) that is not gated by the WR
// toggle and, like WR, renders as a pinned column outside the Display config.
export const RESOURCE_CONTROL_KEYS = [
  SHOW_IN_MAP_KEY,
  WORKFLOW_TYPES_KEY,
  PERMITTED_COMPANIES_KEY,
  HOURS_OF_OPERATION_KEY,
  DATE_CLOSURES_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
] as const

// Materials don't book against hours/closures/duration/overlap — only Workflow
// Types and Permitted Companies (plus the always-present WR toggle).
export const MATERIAL_CONTROL_KEYS = [
  WORKFLOW_TYPES_KEY,
  PERMITTED_COMPANIES_KEY,
] as const

// A project's site root is the project, not a place inside it: nothing is booked against
// it, so no company is permitted on it and no booking limit applies.
export const PROJECT_LOCATION_DENIED_CONTROL_KEYS: readonly string[] = [
  PERMITTED_COMPANIES_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
]

// Module-level like MATERIAL_CONTROL_KEYS, not filtered per render: `controls` is closed
// over by the Controls-tab draft's save callback, so a fresh array identity would rebuild
// it on every keystroke in the card.
export const PROJECT_LOCATION_CONTROL_KEYS: readonly string[] =
  RESOURCE_CONTROL_KEYS.filter(
    key => !PROJECT_LOCATION_DENIED_CONTROL_KEYS.includes(key),
  )

/**
 * The kind slug of a record, when it is a location. Null for every other resource type:
 * `data.type` is the property name of whichever taxonomy the row's type owns, so on an
 * equipment row it holds an equipment slug, and reading it without the type check is the
 * one-field-two-jobs mistake the kind collapse exists to undo.
 */
function locationKindOf(
  resourceTypeId: string | undefined,
  record: { data?: Record<string, any> } | null | undefined,
): unknown {
  return resourceTypeId === ResourceTypeId.LOCATIONS
    ? record?.data?.[LOCATION_KIND_PROPERTY]
    : null
}

/**
 * Whether one control is denied to this record outright, as opposed to merely unset.
 *
 * What the controls TABLE asks: it keeps the column for every other location and prints
 * "Not permitted" in this row's cell, so it needs the per-key answer rather than a
 * narrowed list.
 */
export function isControlDeniedForRecord(
  controlKey: string,
  resourceTypeId: string | undefined,
  record: { data?: Record<string, any> } | null | undefined,
): boolean {
  return (
    isProjectLocationRecord(resourceTypeId, record) &&
    PROJECT_LOCATION_DENIED_CONTROL_KEYS.includes(controlKey)
  )
}

/** Whether this record is the project's single site root. */
export function isProjectLocationRecord(
  resourceTypeId: string | undefined,
  record: { data?: Record<string, any> } | null | undefined,
): boolean {
  return isProjectLocationKind(locationKindOf(resourceTypeId, record) as string)
}

/**
 * Whether a SELECTION contains the site root.
 *
 * Bulk actions are all-or-nothing over their selection - there is no per-row skip a toolbar
 * button can express - so one denied row denies the action. Returns a single boolean on
 * purpose: WHICH buttons that disables is each bar's own business, and Delete is not a
 * control key, so it must not be smuggled into PROJECT_LOCATION_DENIED_CONTROL_KEYS, which
 * is the control vocabulary behind PROJECT_LOCATION_CONTROL_KEYS and the card's save gate.
 */
export function selectionIncludesProjectLocation(
  resourceTypeId: string | undefined,
  records: readonly { id: string; data?: Record<string, any> }[],
  selectedIds: ReadonlySet<string>,
): boolean {
  if (resourceTypeId !== ResourceTypeId.LOCATIONS || selectedIds.size === 0) {
    return false
  }

  return records.some(
    record =>
      selectedIds.has(record.id) &&
      isProjectLocationRecord(resourceTypeId, record),
  )
}

/**
 * The controls ONE record exposes - the type's set, minus anything its kind forbids.
 *
 * Record-level, unlike {@link getResourceControlsForType}: the site root is one row of the
 * Locations table rather than a resource type of its own, so the type-level set cannot
 * express it. Narrowing the list also closes the save path, since useResourceControlsDraft
 * gates each mutation slice on `controls.includes(...)`.
 */
export function getResourceControlsForRecord(
  resourceTypeId: string,
  record: { data?: Record<string, any> } | null | undefined,
): readonly string[] {
  if (isProjectLocationRecord(resourceTypeId, record)) {
    return PROJECT_LOCATION_CONTROL_KEYS
  }

  return getResourceControlsForType(resourceTypeId)
}

/** The controls a resource type exposes in the Controls tab / table. */
export function getResourceControlsForType(
  resourceTypeId: string,
): readonly string[] {
  return resourceTypeId === ResourceTypeId.MATERIALS
    ? MATERIAL_CONTROL_KEYS
    : RESOURCE_CONTROL_KEYS
}

/** Equipment, Materials and Locations expose a Controls tab. */
export function resourceTypeSupportsControls(resourceTypeId: string): boolean {
  return (
    resourceTypeId === ResourceTypeId.MATERIALS ||
    resourceTypeId === ResourceTypeId.OFFLOADING_EQUIPMENT ||
    resourceTypeId === ResourceTypeId.LOCATIONS
  )
}

// A control value of [] means "All" (current and future items). Collapse a
// selection that already covers the whole universe back to [] so items added
// later stay in the "All" set. Shared by the controls table and the Controls tab.
export function normalizeAllToEmpty(
  selectedIds: string[],
  universe: { id: string }[],
): string[] {
  return normalizeAllToEmptyIds(
    selectedIds,
    universe.map(item => item.id),
  )
}

// Same rule for a universe already reduced to ids. Deliberately "covers every"
// rather than set-equality: a selection may carry ids outside the universe —
// e.g. a workflow type deleted since it was saved — and those extras must not
// block the collapse, which set-equality would leave stuck as an explicit list.
export function normalizeAllToEmptyIds(
  selectedIds: string[],
  universeIds: string[],
): string[] {
  // Too few to cover it — `universeIds` is mapped off a record set, so it has no
  // duplicates and a shorter selection can't contain all of it. This is the
  // common case (a real subset), and it keeps it O(1).
  if (universeIds.length === 0 || selectedIds.length < universeIds.length) {
    return selectedIds
  }
  // Set, not `Array.includes`: the case worth detecting is full coverage, which
  // is exactly the one where a linear scan never short-circuits.
  const selected = new Set(selectedIds)
  return universeIds.every(id => selected.has(id)) ? [] : selectedIds
}
